// controllers/editor_controller.js
import { Controller } from "@hotwired/stimulus"
import Quill from "quill"

export default class extends Controller {
    static values = {
        editorId: String,
        placeholder: String,
        hiddenId: String,      // optional: id of the hidden input to sync into
        debounce: Number       // optional: ms (default 150)
    }

    connect() {
        const id = this.editorIdValue || this.element.id
        const placeholder = this.placeholderValue || ""
        const editorEl = document.getElementById(id)
        const toolbarEl = document.getElementById(`toolbar_${id}`)
        if (!editorEl) {
            console.warn(`No element found with id "${id}" for Quill editor.`)
            return
        }

        // Resolve the hidden input we’ll sync into
        // Priority:
        // 1) data-editor-hidden-id-value
        // 2) input[type=hidden][id === id without 'editor_' prefix]
        // 3) input[type=hidden][name === id without 'editor_' prefix]
        this.hiddenEl = this.#resolveHiddenInput(id)

        // Init Quill
        this.quill = new Quill(editorEl, {
            theme: "snow",
            placeholder,
            modules: { toolbar: toolbarEl }
        })

        window.__quillById ||= {}
        window.__quillById[id] = this.quill

        // Initial sync (if hidden already has content)
        if (this.hiddenEl && this.hiddenEl.value) {
            this.quill.clipboard.dangerouslyPasteHTML(this.hiddenEl.value)
        }

        // Live sync with debounce
        const delay = Number.isFinite(this.debounceValue) ? this.debounceValue : 150
        const debouncedSync = this.#debounce(() => this.#syncHidden(), delay)
        this.quill.on("text-change", debouncedSync)

        // Ensure sync before submit (belt & suspenders)
        this.formEl = this.element.closest("form")
        if (this.formEl) {
            this._onFormSubmit = () => this.#syncHidden(true)
            this.formEl.addEventListener("submit", this._onFormSubmit)
        }

        editorEl.dispatchEvent(new CustomEvent("quill:ready", { detail: { id, quill: this.quill } }))
    }

    disconnect() {
        if (this.formEl && this._onFormSubmit) {
            this.formEl.removeEventListener("submit", this._onFormSubmit)
        }
        if (window.__quillById && this.editorIdValue in window.__quillById) {
            delete window.__quillById[this.editorIdValue]
        }
    }

    // --- private ---

    #resolveHiddenInput(editorId) {
        // explicit id via data-editor-hidden-id-value
        if (this.hasHiddenIdValue) {
            return document.getElementById(this.hiddenIdValue)
        }

        // infer from id: "editor_mission" -> "mission"
        const inferred = editorId.startsWith("editor_") ? editorId.replace(/^editor_/, "") : editorId

        // try by id first
        let el = document.getElementById(inferred)
        if (el && el.type === "hidden") return el

        // try by name
        el = this.element.closest("form")?.querySelector(`input[type="hidden"][name="${inferred}"]`)
        if (el) return el

        console.warn(`No hidden input found for editor "${editorId}". Set data-editor-hidden-id-value or add <input type="hidden" id="${inferred}" name="${inferred}">`)
        return null
    }

    #syncHidden(force = false) {
        if (!this.hiddenEl) return
        // HTML output (most common for Quill)
        const html = this.quill.root.innerHTML
        if (force || this.hiddenEl.value !== html) {
            this.hiddenEl.value = html
        }
    }

    #debounce(fn, wait) {
        let t
        return (...args) => {
            clearTimeout(t)
            t = setTimeout(() => fn.apply(this, args), wait)
        }
    }
}